import React, { useState, useEffect, useCallback, useRef } from 'react';
import { HoverCard, HoverCardTrigger, HoverCardContent } from './ui/hover-card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { Loader2 } from 'lucide-react';
import { getProxyImageUrl } from '../utils';

// ─── In-memory card cache (shared across all CardCode instances) ──
const cardCache = new Map();

/**
 * CardCode – renders an inline interactive card reference.
 *
 *   <CardCode cardNumber="BP05-044" />                  → shows "BP05-044"
 *   <CardCode cardNumber="BP05-044">Mefilas</CardCode>  → shows "Mefilas"
 *
 * • Hover → HoverCard with thumbnail + basic stats
 * • Click → Full-size Dialog modal with card image and details
 * • Fetches data from /api/cards/number/:number and caches the result.
 */
export default function CardCode({ cardNumber, lang = 'EN', children }) {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const fetchedRef = useRef(false);

  const fetchCard = useCallback(async () => {
    if (!cardNumber) return;

    // Return cached data instantly
    const cacheKey = `${cardNumber}__${lang}`;
    if (cardCache.has(cacheKey)) {
      setCard(cardCache.get(cacheKey));
      return;
    }

    if (fetchedRef.current) return;
    fetchedRef.current = true;
    setLoading(true);

    try {
      const res = await fetch(
        `/api/cards/number/${encodeURIComponent(cardNumber)}?lang=${lang}`
      );
      if (!res.ok) throw new Error('not found');
      const data = await res.json();
      cardCache.set(cacheKey, data);
      setCard(data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [cardNumber, lang]);

  // Pre-fetch when the component mounts (lightweight – uses cache on repeat)
  useEffect(() => {
    fetchCard();
  }, [fetchCard]);

  const imageUrl = card
    ? getProxyImageUrl(
        card.images?.blob?.full || card.images?.original?.full
      )
    : '';

  const formatRarity = (rarity) => {
    if (!rarity) return '';
    const val = typeof rarity === 'object' ? rarity.value : rarity;
    if (!val) return '';
    const parts = String(val).split('_');
    return (parts[parts.length - 1] || '').toUpperCase();
  };

  // ── Popover preview card ──────────────────────────────────
  const PreviewContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center p-4">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      );
    }
    if (error || !card) {
      return (
        <div className="text-sm text-muted-foreground p-2">Card not found</div>
      );
    }
    return (
      <div className="flex gap-3 max-w-xs">
        {imageUrl && (
          <img
            src={imageUrl}
            alt={card.name}
            className="w-24 h-auto rounded-md object-contain flex-shrink-0"
            loading="lazy"
          />
        )}
        <div className="flex flex-col gap-1 min-w-0">
          <p className="text-sm font-semibold leading-tight truncate">
            {card.name}
          </p>
          {card.characterName && (
            <p className="text-xs text-muted-foreground truncate">
              {card.characterName}
            </p>
          )}
          <div className="flex flex-wrap gap-1 mt-1">
            {card.level != null && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                Lv.{card.level}
              </Badge>
            )}
            {card.rarity && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                {formatRarity(card.rarity)}
              </Badge>
            )}
            {card.type && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                {typeof card.type === 'object' ? card.type.value : card.type}
              </Badge>
            )}
          </div>
          {card.effect && (
            <p className="text-[11px] text-muted-foreground mt-1 line-clamp-3 leading-snug">
              {card.effect}
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Inline link with HoverCard preview on hover, click opens full modal */}
      <HoverCard openDelay={150} closeDelay={100}>
        <HoverCardTrigger asChild>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              setShowModal(true);
            }}
            className={`inline text-primary underline underline-offset-2 decoration-primary/40 hover:decoration-primary cursor-pointer bg-transparent border-none p-0 m-0 leading-inherit${
              children ? '' : ' font-mono text-[0.92em]'
            }`}
          >
            {children ?? cardNumber}
          </button>
        </HoverCardTrigger>
        <HoverCardContent
          side="top"
          align="start"
          className="w-auto max-w-sm p-3"
        >
          <PreviewContent />
        </HoverCardContent>
      </HoverCard>

      {/* Full card modal on click */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{card?.name || cardNumber}</DialogTitle>
          </DialogHeader>
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}
          {error && (
            <p className="text-center text-muted-foreground py-8">
              Could not load card data for {cardNumber}.
            </p>
          )}
          {card && !loading && (
            <div className="flex flex-col items-center gap-4">
              {imageUrl && (
                <img
                  src={imageUrl}
                  alt={card.name}
                  className="w-64 h-auto rounded-lg shadow-md"
                />
              )}
              <div className="w-full space-y-2 text-sm">
                {card.characterName && (
                  <p>
                    <span className="text-muted-foreground">Character:</span>{' '}
                    {card.characterName}
                  </p>
                )}
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline">{cardNumber}</Badge>
                  {card.level != null && (
                    <Badge variant="secondary">Lv.{card.level}</Badge>
                  )}
                  {card.rarity && (
                    <Badge variant="secondary">
                      {formatRarity(card.rarity)}
                    </Badge>
                  )}
                  {card.type && (
                    <Badge variant="outline">
                      {typeof card.type === 'object'
                        ? card.type.value
                        : card.type}
                    </Badge>
                  )}
                </div>
                {card.effect && (
                  <div className="bg-muted/50 rounded-md p-3 text-sm leading-relaxed">
                    {card.effect}
                  </div>
                )}
                {card.flavorText && (
                  <div className="bg-muted/30 rounded-md p-3 text-sm italic text-muted-foreground">
                    "{card.flavorText}"
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
